import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Calendar, ChevronRight, Clock } from 'lucide-react';
import { meetingsApi } from '../../services/api';
import EmptyState from '../shared/EmptyState';
import Modal from '../shared/Modal';
import { formatDateRelative, MEETING_TYPE_LABELS } from '../../utils/formatters';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const MEETING_COLORS: Record<string, string> = {
  presidencia: 'bg-blue-500',
  sumo_conselho: 'bg-purple-500',
  conselho_estaca: 'bg-teal-500',
};

type TabType = 'upcoming' | 'history';

export default function Meetings() {
  const [tab, setTab] = useState<TabType>('upcoming');
  const [showNewModal, setShowNewModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['meetings', { status: tab === 'upcoming' ? 'upcoming' : 'completed' }],
    queryFn: () => meetingsApi.list({ status: tab === 'upcoming' ? 'upcoming' : 'completed' }),
  });

  const meetings = (data?.data || []) as any[];

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4">
        {(['upcoming', 'history'] as TabType[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'flex-1 py-2 rounded-lg text-sm font-medium transition-colors',
              tab === t ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            )}
          >
            {t === 'upcoming' ? 'Próximas' : 'Histórico'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="card animate-pulse h-20" />)}
        </div>
      ) : meetings.length === 0 ? (
        <EmptyState
          icon={Calendar}
          title={tab === 'upcoming' ? 'Nenhuma reunião agendada' : 'Histórico vazio'}
          description={tab === 'upcoming' ? 'Use o botão + para criar uma nova reunião' : undefined}
        />
      ) : (
        <div className="space-y-3">
          {meetings.map((meeting) => (
            <Link
              key={meeting.id}
              to={`/reunioes/${meeting.id}`}
              className="card block hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3">
                <div className={clsx(
                  'w-2 h-12 rounded-full flex-shrink-0',
                  MEETING_COLORS[meeting.type] || 'bg-gray-400'
                )} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-medium text-gray-800 truncate">
                      {MEETING_TYPE_LABELS[meeting.type] || meeting.type}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{formatDateRelative(meeting.date)}</span>
                    {meeting.time && (
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {meeting.time}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {meeting.agenda_count || 0} {(meeting.agenda_count || 0) === 1 ? 'item' : 'itens'}
                    </span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* FAB */}
      <button
        onClick={() => setShowNewModal(true)}
        className="fixed bottom-28 right-4 w-14 h-14 bg-brand-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-brand-600 transition-colors z-30"
        aria-label="Nova reunião"
      >
        <Plus size={24} />
      </button>

      <NewMeetingModal isOpen={showNewModal} onClose={() => setShowNewModal(false)} />
    </div>
  );
}

function NewMeetingModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [type, setType] = useState('presidencia');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => meetingsApi.create({ type, date, time: time || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      toast.success('Reunião criada!');
      onClose();
      setDate('');
      setTime('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nova Reunião"
      footer={
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">
            Cancelar
          </button>
          <button
            type="submit"
            form="new-meeting-form"
            disabled={!date || mutation.isPending}
            className="btn-primary flex-1"
          >
            {mutation.isPending ? 'Criando...' : 'Criar Reunião'}
          </button>
        </div>
      }
    >
      <form
        id="new-meeting-form"
        onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
        className="space-y-4"
      >
        <div>
          <label className="label">Tipo de reunião</label>
          <select value={type} onChange={e => setType(e.target.value)} className="input">
            <option value="presidencia">Presidência da Estaca</option>
            <option value="sumo_conselho">Sumo Conselho</option>
            <option value="conselho_estaca">Conselho da Estaca</option>
          </select>
        </div>
        <div>
          <label className="label">Data <span className="text-red-500">*</span></label>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="input"
            required
          />
        </div>
        <div>
          <label className="label">Horário (opcional)</label>
          <input
            type="time"
            value={time}
            onChange={e => setTime(e.target.value)}
            className="input"
          />
        </div>
      </form>
    </Modal>
  );
}
